import React, { useReducer, useState } from 'react';
import proptypes from 'prop-types';
import { Overlay } from '@thd-olt-component-react/core-ui';
import { Button, IconButton, Typography } from '@one-thd/sui-atomic-components';
import { ArrowBack, Close } from '@one-thd/sui-icons';
import { DisplayName, EmailAddress, Footer, QAField } from './component/index';
import { triggerAnalytics } from '../../Analytics';
import { useSubmitAnswer, useSubmitQuestion } from '../../hooks/index';
import { notEmptyString, validateEmail } from './helper/QAModalFormHelper';
import { dataModel } from './dataModel';
import { ImageUpload } from './component/ImageUpload';
import './QAModalForm.scss';
import { DeviceDetection } from '../DeviceDetection/DeviceDetection';
import { QAModalFormSuccess } from './QAModalFormSuccess.component';

const { func, string, bool } = proptypes;

const initialState = {
  question: '',
  answer: '',
  displayName: '',
  email: '',
  images: [],
  displayNameErrorText: '',
  questionErrorText: '',
  answerErrorText: '',
  emailErrorText: ''
};

function reducer(state, { action, value }) {
  switch (action) {
  case 'question':
    return { ...state, question: value };
  case 'answer':
    return { ...state, answer: value };
  case 'displayName':
    return { ...state, displayName: value };
  case 'email':
    return { ...state, email: value };
  case 'questionError':
    return { ...state, questionErrorText: value };
  case 'answerError':
    return { ...state, answerErrorText: value };
  case 'displayNameError':
    return { ...state, displayNameErrorText: value };
  case 'emailError':
    return { ...state, emailErrorText: value };
  case 'images':
    return { ...state, images: value };
  default:
    return state;
  }
}

const QAModal = ({
  itemId,
  openModal,
  closeModal,
  headerText,
  questionText,
  userName,
  askedDate,
  channel,
  questionId
}) => {
  // Pre-populate email address for a signed in users
  initialState.email = window?.THDCustomer?.default?.email || '';
  const [deviceFingerPrint, setDeviceFingerPrint] = useState(null);
  const [state, dispatch] = useReducer(reducer, initialState);
  const {
    question,
    answer,
    displayName,
    email,
    displayNameErrorText,
    questionErrorText,
    answerErrorText,
    emailErrorText,
    images
  } = state;
  const formInputClasses = 'qa-modal';
  const from = questionText ? 'answer' : 'question';
  const postQuestionData = {
    itemId,
    questionSummary: question,
    userNickName: displayName,
    userEmail: email,
    userId: window?.THDCustomer?.default?.userID || '',
    action: 'submit',
    sendEmailAlertWhenPublished: 'yes',
    deviceFingerPrint
  };

  const postAnswerData = {
    questionId,
    answerText: answer,
    userNickName: displayName,
    userEmail: email,
    userId: window?.THDCustomer?.default?.userID || '',
    action: 'submit',
    deviceFingerPrint,
    sendEmailAlertWhenPublished: 'yes'
  };

  const [submitQuestionForm, questionResponse] = useSubmitQuestion(postQuestionData);
  const [submitAnswerForm, answerResponse] = useSubmitAnswer(postAnswerData);

  const onSubmit = () => {
    dispatch({
      action: 'questionError',
      value:
        !notEmptyString(questionText) && !notEmptyString(state.question) && !state.question
          ? 'Please provide your question here.'
          : ''
    });
    dispatch({
      action: 'answerError',
      value:
        notEmptyString(questionText) && !notEmptyString(state.answer) && !state.answer
          ? 'Please provide your answer here.'
          : ''
    });
    dispatch({
      action: 'emailError',
      value: !notEmptyString(state.email) ? 'Please provide your email.' : ''
    });
    dispatch({
      action: 'displayNameError',
      value:
        !notEmptyString(state.displayName) && !state.displayName
          ? 'Please provide a display name.'
          : ''
    });
    if (
      validateEmail(state.email)
      && ((!notEmptyString(questionText) && notEmptyString(state.question))
        || (notEmptyString(questionText) && notEmptyString(state.answer)))
    ) {
      if (!notEmptyString(questionText)) {
        submitQuestionForm();
      } else {
        submitAnswerForm();
      }
    }

    const payload = {
      component: from === 'answer' ? 'answer a question overlay' : 'ask a question overlay',
      element: 'submit'
    };
    triggerAnalytics(payload);
  };

  const submittedSuccessfully = answerResponse?.data?.submitAnswer?.SubmissionId
    || questionResponse?.data?.submitQuestion?.SubmissionId;

  return (
    <Overlay onClose={closeModal} open={openModal} className="qa-modal-wrapper">
      <DeviceDetection onSuccess={(fingerprint) => setDeviceFingerPrint(fingerprint)} />
      {channel === 'mobile' && !submittedSuccessfully && (
        <Button variant="ghost" startIcon={ArrowBack} onClick={closeModal} data-testid="qa-model-overlay-back">
          Back
        </Button>
      )}
      {submittedSuccessfully ? (
        <QAModalFormSuccess from={from} questionText={questionText} closeModal={closeModal} />
      ) : (
        <div className="qa-modal_content sui-mt-3" data-testid="QAModelOverlay">
          <div className="sui-flex sui-justify-between sui-items-center sui-mb-4 sui-pl-3">
            <Typography variant="h4">{headerText}</Typography>
            {channel !== 'mobile' && (
              <IconButton
                icon={Close}
                color="brand"
                iconSize="large"
                onClick={closeModal}
                data-testid="qa-model-overlay-close"
              />
            )}
          </div>
          {/* QA Modal */}
          <div className="qa-modal">
            {questionText && (
              <div className={`sui-px-3 ${formInputClasses}`}>
                <div className={`qa-modal__review-text ${formInputClasses}`}>
                  <span className="qa-modal__question">Q: {questionText} </span>
                </div>
                <div className="asked-by sui-pb-3">
                  by {userName} <span className="asked-by__divider">|</span> {askedDate}
                </div>
              </div>
            )}
            <QAField
              text={questionText ? 'Answer' : 'Question'}
              placeholder={questionText ? 'Type your answer here' : 'Type your question here'}
              value={questionText ? answer : question}
              validationError={questionText ? answerErrorText : questionErrorText}
              setQAText={(text) => {
                dispatch({ action: from, value: text });
                dispatch({ action: `${from}Error`, value: '' });
              }}
            />
            <ImageUpload
              formInputClasses={formInputClasses}
              images={images}
              setImages={(newImages) => {
                dispatch({ action: 'images', value: newImages });
              }}
              from={from}
            />
            <DisplayName
              displayName={displayName}
              validationError={displayNameErrorText}
              setDisplayName={(text) => {
                dispatch({ action: 'displayName', value: text });
                dispatch({ action: 'displayNameError', value: '' });
              }}
            />
            <EmailAddress
              emailAddress={email}
              validationError={emailErrorText}
              setEmailAddress={(text) => {
                dispatch({ action: 'email', value: text });
                dispatch({ action: 'emailError', value: '' });
              }}
            />
            <Footer formInputClasses={formInputClasses} from={from} onSubmit={onSubmit} />
          </div>
        </div>
      )}
    </Overlay>
  );
};

QAModal.displayName = 'QAModal';

QAModal.propTypes = {
  itemId: string,
  openModal: bool,
  closeModal: func,
  headerText: string,
  questionText: string,
  userName: string,
  askedDate: string,
  channel: string,
  questionId: string
};

QAModal.defaultProps = {
  itemId: '',
  openModal: false,
  closeModal: null,
  headerText: '',
  questionText: '',
  userName: '',
  askedDate: '',
  channel: 'desktop',
  questionId: null
};

QAModal.dataModel = dataModel;

export { QAModal };
